import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  Chip,
  IconButton,
  Collapse,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as ClearIcon,
  Refresh as RefreshIcon,
  Cloud as CloudIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { logService, type LogEntry, type LogLevel } from '../../services/logService';

interface LogViewerProps {
  maxHeight?: string | number;
}


function LogEntryItem({ log }: { log: LogEntry }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = log.details && Object.keys(log.details).length > 0;

  return (
    <ListItem
      sx={{
        flexDirection: 'column',
        alignItems: 'stretch',
        py: 0.75,
        px: 1,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      {/* Top row: Service + Action (left) | Level + Timestamp (right) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: 1,
        }}
      >
        {/* Left: Service + Action */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              fontSize: '0.7rem',
              color: 'primary.main',
              textTransform: 'uppercase',
            }}
          >
            {log.source}
          </Typography>
          {log.action && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                ·
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.65rem',
                  fontFamily: 'monospace',
                  color: 'text.secondary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {log.action}
              </Typography>
            </>
          )}
        </Box>

        {/* Right: Level + Timestamp */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
          {log.level && (
            <Chip
              label={log.level}
              size="small"
              color={log.level === 'error' ? 'error' : log.level === 'warning' ? 'warning' : log.level === 'success' ? 'success' : 'default'}
              sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600 }}
            />
          )}
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', fontFamily: 'monospace' }}>
            {log.timestamp.toLocaleTimeString()}
          </Typography>
          {hasDetails && (
            <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ p: 0.25 }}>
              {expanded ? (
                <ExpandLessIcon sx={{ fontSize: 16 }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Full width message row */}
      <Box sx={{ mt: 0.5, width: '100%' }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.75rem',
            wordBreak: 'break-word',
            lineHeight: 1.4,
          }}
        >
          {log.message}
        </Typography>
      </Box>

      {/* Expandable metadata */}
      {hasDetails && (
        <Collapse in={expanded}>
          <Box
            sx={{
              mt: 1,
              p: 1,
              borderRadius: 1,
              backgroundColor: 'action.hover',
              fontFamily: 'monospace',
              fontSize: '0.7rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: 200,
              overflow: 'auto',
            }}
          >
            {JSON.stringify(log.details, null, 2)}
          </Box>
        </Collapse>
      )}
    </ListItem>
  );
}

type LevelFilter = 'all' | 'info' | 'success' | 'warning' | 'error';
type SourceFilter = 'all' | 'ai' | 'db' | 'game' | 'ui' | 'mcp' | 'vector' | 'init' | 'R2';

export function LogViewer({ maxHeight = '100%' }: LogViewerProps) {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [levelFilter, setLevelFilter] = useState<LevelFilter>('all');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    // Subscribe to log updates
    const unsubscribe = logService.subscribe((newLogs) => {
      setLogs(newLogs);
    });

    // Start auto-refresh if enabled
    if (autoRefresh) {
      logService.startAutoRefresh(5000); // Refresh every 5 seconds
    }

    return () => {
      unsubscribe();
      logService.stopAutoRefresh();
    };
  }, [autoRefresh]);

  const handleLevelFilterChange = (_: React.MouseEvent<HTMLElement>, newFilter: LevelFilter) => {
    if (newFilter !== null) {
      setLevelFilter(newFilter);
    }
  };

  const handleSourceFilterChange = (_: React.MouseEvent<HTMLElement>, newFilter: SourceFilter) => {
    if (newFilter !== null) {
      setSourceFilter(newFilter);
    }
  };

  const handleClear = async () => {
    await logService.clearAllLogs();
  };

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    try {
      await logService.fetchBackendLogs();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleAutoRefreshToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAutoRefresh(event.target.checked);
  };

  const handleDownloadLogs = () => {
    // Get all logs (both frontend and backend)
    const allLogs = logService.getLogs();

    // Format as JSON with pretty printing
    const logsJson = JSON.stringify(allLogs, null, 2);

    // Create blob and download
    const blob = new Blob([logsJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `space-antics-logs-${new Date().toISOString()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Filter logs by level and source
  const filteredLogs = logs.filter((log) => {
    // Apply level filter
    const levelMatch =
      levelFilter === 'all' ||
      log.level === levelFilter ||
      (levelFilter === 'error' && log.status === 'error');

    // Apply source filter with composite support
    // e.g., "ai" matches both "ai" and "ai+mcp"
    // "mcp" matches any source containing "+mcp"
    // "vector" matches any source containing "+vector"
    // "init" matches any source containing "+init"
    // "R2" matches any source containing "+R2"
    let sourceMatch = false;
    if (sourceFilter === 'all') {
      sourceMatch = true;
    } else if (sourceFilter === 'mcp' || sourceFilter === 'vector' || sourceFilter === 'init' || sourceFilter === 'R2') {
      // Composite filter: show all logs with "+{filter}" in source or exact match
      sourceMatch = log.source.includes(`+${sourceFilter}`) || log.source === sourceFilter;
    } else {
      // Regular filter: match exact or composite (e.g., "ai" matches "ai" and "ai+mcp")
      sourceMatch = log.source === sourceFilter || log.source.startsWith(`${sourceFilter}+`);
    }

    return levelMatch && sourceMatch;
  });

  // Count by level and source
  const levelCounts = {
    all: logs.length,
    info: logs.filter((l) => l.level === 'info').length,
    success: logs.filter((l) => l.level === 'success').length,
    warning: logs.filter((l) => l.level === 'warning').length,
    error: logs.filter((l) => l.level === 'error' || l.status === 'error').length,
  };

  const sourceCounts = {
    all: logs.length,
    ai: logs.filter((l) => l.source === 'ai' || l.source.startsWith('ai+')).length,
    db: logs.filter((l) => l.source === 'db' || l.source.startsWith('db+')).length,
    game: logs.filter((l) => l.source === 'game' || l.source.startsWith('game+')).length,
    ui: logs.filter((l) => l.source === 'ui' || l.source.startsWith('ui+')).length,
    mcp: logs.filter((l) => l.source.includes('+mcp')).length,
    vector: logs.filter((l) => l.source.includes('+vector') || l.source === 'vector').length,
    init: logs.filter((l) => l.source.includes('+init') || l.source === 'init').length,
    R2: logs.filter((l) => l.source.includes('+R2') || l.source === 'R2').length,
  };

  return (
    <Card
      className="panel"
      sx={{
        backgroundColor: 'background.paper',
        border: 1,
        borderColor: 'divider',
      }}
    >
      <CardContent className="panel-header" sx={{ p: 1, pb: 0, '&:last-child': { pb: 0 } }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 700,
                letterSpacing: 1,
                fontSize: '0.65rem',
              }}
            >
              System Logs ({filteredLogs.length})
            </Typography>
            <Tooltip title="Includes frontend API calls (this browser) and backend service logs (database)">
              <Chip
                icon={<CloudIcon sx={{ fontSize: 12 }} />}
                label="Frontend + Backend"
                size="small"
                variant="outlined"
                color="info"
                sx={{ height: 16, fontSize: '0.55rem', ml: 0.5 }}
              />
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoRefresh}
                  onChange={handleAutoRefreshToggle}
                  size="small"
                />
              }
              label={<Typography sx={{ fontSize: '0.65rem' }}>Auto</Typography>}
              sx={{ mr: 0, ml: 0 }}
            />
            <Tooltip title="Refresh backend logs">
              <IconButton size="small" onClick={handleManualRefresh} disabled={isRefreshing}>
                <RefreshIcon
                  sx={{
                    fontSize: 16,
                    animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
              </IconButton>
            </Tooltip>
            <Tooltip title="Clear all logs (frontend + backend)">
              <IconButton size="small" onClick={handleClear}>
                <ClearIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download logs as JSON">
              <IconButton size="small" onClick={handleDownloadLogs}>
                <DownloadIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Level filter buttons */}
        <Box sx={{ mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', mb: 0.25, display: 'block' }}>
            Level:
          </Typography>
          <ToggleButtonGroup
            value={levelFilter}
            exclusive
            onChange={handleLevelFilterChange}
            size="small"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              '& .MuiToggleButton-root': {
                py: 0.25,
                px: 1,
                fontSize: '0.65rem',
              },
            }}
          >
            <ToggleButton value="all">All ({levelCounts.all})</ToggleButton>
            <ToggleButton value="info">Info ({levelCounts.info})</ToggleButton>
            <ToggleButton value="success">Success ({levelCounts.success})</ToggleButton>
            <ToggleButton value="warning">Warning ({levelCounts.warning})</ToggleButton>
            <ToggleButton value="error">Error ({levelCounts.error})</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Source filter buttons */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', mb: 0.25, display: 'block' }}>
            Source:
          </Typography>
          <ToggleButtonGroup
            value={sourceFilter}
            exclusive
            onChange={handleSourceFilterChange}
            size="small"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              '& .MuiToggleButton-root': {
                py: 0.25,
                px: 1,
                fontSize: '0.65rem',
              },
            }}
          >
            <ToggleButton value="all">All ({sourceCounts.all})</ToggleButton>
            <ToggleButton value="ai">AI ({sourceCounts.ai})</ToggleButton>
            <ToggleButton value="db">DB ({sourceCounts.db})</ToggleButton>
            <ToggleButton value="game">Game ({sourceCounts.game})</ToggleButton>
            <ToggleButton value="ui">UI ({sourceCounts.ui})</ToggleButton>
            <ToggleButton value="mcp">MCP ({sourceCounts.mcp})</ToggleButton>
            <ToggleButton value="vector">Vector ({sourceCounts.vector})</ToggleButton>
            <ToggleButton value="init">Init ({sourceCounts.init})</ToggleButton>
            <ToggleButton value="R2">R2 ({sourceCounts.R2})</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </CardContent>

      <Divider />

      {/* Log list */}
      <Box
        className="panel-scroll"
        sx={{
          maxHeight,
        }}
      >
        {filteredLogs.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              minHeight: 100,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No logs yet
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {filteredLogs.map((log) => (
              <LogEntryItem key={log.id} log={log} />
            ))}
          </List>
        )}
      </Box>
    </Card>
  );
}
